import React, { useRef, useState, useMemo, useEffect } from 'react';
import '../css/DeploymentModal.css';
import '../css/Forms.css'; // reuses "po-*" progress/footer styles

export default function DeploymentModal({ isOpen, onClose }) {
  const formRef = useRef(null);

  // -------- Draft helpers --------
  const draftKey = 'DEPLOYMENT_DRAFT_V2';
  const loadJSON = (k, f = null) => { try { const v = localStorage.getItem(k); return v ? JSON.parse(v) : f; } catch { return f; } };
  const saveJSON = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch {} };

  // -------- Section 1: Deployment Type --------
  const [deployType, setDeployType] = useState(''); // 'standard' | 'fast' | 'white-label'
  const [expectedDuration, setExpectedDuration] = useState('Standard [30 days]');
  const [engagementDuration, setEngagementDuration] = useState('Mid-Term [6–12 months]');
  const [industryProjectType, setIndustryProjectType] = useState('Game Development');
  const [engagementModelAlignment, setEngagementModelAlignment] = useState('Linked to Pre-Onboarding');
  const [reportingLine, setReportingLine] = useState('Client PM');
  const [reportingLineOther, setReportingLineOther] = useState('');

  // -------- Section 2: Talent Requirements --------
  const rolesCatalog = useMemo(
    () => [
      '2D Game Artist', '3D Artist', 'Animator', 'Unity Developer', 'Unreal Developer',
      'QA Tester', 'Technical Artist', 'Game Designer', 'Game Producer', 'Sound Designer',
      'Game Technical Support', 'DevOps Engineer'
    ],
    []
  );
  const [roles, setRoles] = useState([]); // multi-select
  const [talentsNeeded, setTalentsNeeded] = useState('');
  const [timeline, setTimeline] = useState(''); // 'urgent' | 'standard' | 'flexible'
  const [targetStart, setTargetStart] = useState('');
  const [seniorityLevel, setSeniorityLevel] = useState('Mid-Level');
  const [workArrangement, setWorkArrangement] = useState('Remote');
  const [shiftRequirement, setShiftRequirement] = useState('Day');
  const [languagesRequired, setLanguagesRequired] = useState([]);
  const [toolsSoftwareRequired, setToolsSoftwareRequired] = useState([]);
  const [toolsSoftwareOther, setToolsSoftwareOther] = useState('');
  const [securityComplianceNeeds, setSecurityComplianceNeeds] = useState([]);
  const [securityComplianceOther, setSecurityComplianceOther] = useState('');
  const [portfolioReviewRequired, setPortfolioReviewRequired] = useState('Yes');

  // -------- Section 3: Training & Development --------
  const [training, setTraining] = useState({
    companyOrientation: false,
    clientProjectOrientation: false,
    systemsToolsTraining: false,
    systemsToolsDetails: '',
    roleSpecificSkillsTraining: false,
    roleSpecificSkillsDetails: '',
    softSkillsDevelopment: false,
    practicalApplicationSimulation: false,
    practicalApplicationDetails: '',
    assessmentEvaluation: false,
    assessmentEvaluationFile: null,
    trainingDeliveryMethod: 'Virtual',
    trainingDuration: 1,
    evaluationMethod: 'Both',
    certificationRequired: 'No',
    customTrainingModules: '',
    customTrainingModulesFile: null,
  });

  // -------- Section 4: Nesting & Go-Live --------
  const [nestingDuration, setNestingDuration] = useState('null'); // '1w' | '2w' | 'custom'
  const [nestingCustomWeeks, setNestingCustomWeeks] = useState(3);
  const [supervision, setSupervision] = useState('null'); // 'light' | 'moderate' | 'intensive'
  const [readiness, setReadiness] = useState('null'); // 'client' | 'exodia'
  const [performanceMonitoringFrequency, setPerformanceMonitoringFrequency] = useState('Weekly');
  const [clientInvolvementInNesting, setClientInvolvementInNesting] = useState('Partial');
  const [knowledgeTransferMethod, setKnowledgeTransferMethod] = useState('Documentation');
  const [escalationProtocolDuringGoLive, setEscalationProtocolDuringGoLive] = useState('Weekly reports');

  // -------- Section 5: White Label & Branding --------
  const [whiteLabel, setWhiteLabel] = useState(false);
  const [brandingNotes, setBrandingNotes] = useState('');

  // -------- Section 6: Budget & Notes --------
  const [budgetRange, setBudgetRange] = useState('');
  const [maxBudgetCap, setMaxBudgetCap] = useState('');
  const [paymentPreference, setPaymentPreference] = useState('Monthly');
  const [specialInstructions, setSpecialInstructions] = useState('');
  const [top3DeploymentGoals, setTop3DeploymentGoals] = useState('');
  const [successMetricsKPIs, setSuccessMetricsKPIs] = useState('');
  const [pointOfContact, setPointOfContact] = useState({
    fullName: '',
    email: '',
    phoneWhatsapp: '',
  });

  // -------- Steps / Progress / Errors --------
  const [step, setStep] = useState(0);
  const [errors, setErrors] = useState({});
  const STEPS = useMemo(() => ([
    { key: 'type',       title: 'Deployment Type' },
    { key: 'talent',     title: 'Talent Requirements' },
    { key: 'training',   title: 'Training & Development' },
    { key: 'nesting',    title: 'Nesting & Go-Live' },
    { key: 'branding',   title: 'White Label & Branding' },
    { key: 'budget',     title: 'Budget & Notes' },
  ]), []);
  const totalSteps = STEPS.length;
  const progressPct = Math.round(((step + 1) / totalSteps) * 100);

  // -------- Load draft on open --------
  useEffect(() => {
    if (!isOpen) return;
    const d = loadJSON(draftKey, null);
    if (!d) return;

    // Section 1: Deployment Type
    setDeployType(d.deployType ?? '');
    setExpectedDuration(d.expectedDuration ?? 'Standard [30 days]');
    setEngagementDuration(d.engagementDuration ?? 'Mid-Term [6–12 months]');
    setIndustryProjectType(d.industryProjectType ?? 'Game Development');
    setEngagementModelAlignment(d.engagementModelAlignment ?? 'Linked to Pre-Onboarding');
    setReportingLine(d.reportingLine ?? 'Client PM');
    setReportingLineOther(d.reportingLineOther ?? '');

    // Section 2: Talent Requirements
    setRoles(Array.isArray(d.roles) ? d.roles : []);
    setTalentsNeeded(d.talentsNeeded ?? '');
    setTimeline(d.timeline ?? '');
    setTargetStart(d.targetStart ?? '');
    setSeniorityLevel(d.seniorityLevel ?? 'Mid-Level');
    setWorkArrangement(d.workArrangement ?? 'Remote');
    setShiftRequirement(d.shiftRequirement ?? 'Day');
    setLanguagesRequired(Array.isArray(d.languagesRequired) ? d.languagesRequired : []);
    setToolsSoftwareRequired(Array.isArray(d.toolsSoftwareRequired) ? d.toolsSoftwareRequired : []);
    setToolsSoftwareOther(d.toolsSoftwareOther ?? '');
    setSecurityComplianceNeeds(Array.isArray(d.securityComplianceNeeds) ? d.securityComplianceNeeds : []);
    setSecurityComplianceOther(d.securityComplianceOther ?? '');
    setPortfolioReviewRequired(d.portfolioReviewRequired ?? 'Yes');

    // Section 3: Training & Development
    setTraining({
      companyOrientation: !!d?.training?.companyOrientation,
      clientProjectOrientation: !!d?.training?.clientProjectOrientation,
      systemsToolsTraining: !!d?.training?.systemsToolsTraining,
      systemsToolsDetails: d?.training?.systemsToolsDetails ?? '',
      roleSpecificSkillsTraining: !!d?.training?.roleSpecificSkillsTraining,
      roleSpecificSkillsDetails: d?.training?.roleSpecificSkillsDetails ?? '',
      softSkillsDevelopment: !!d?.training?.softSkillsDevelopment,
      practicalApplicationSimulation: !!d?.training?.practicalApplicationSimulation,
      practicalApplicationDetails: d?.training?.practicalApplicationDetails ?? '',
      assessmentEvaluation: !!d?.training?.assessmentEvaluation,
      assessmentEvaluationFile: d?.training?.assessmentEvaluationFile ?? null,
      trainingDeliveryMethod: d?.training?.trainingDeliveryMethod ?? 'Virtual',
      trainingDuration: Number.isFinite(+d?.training?.trainingDuration) ? +d.training.trainingDuration : 1,
      evaluationMethod: d?.training?.evaluationMethod ?? 'Both',
      certificationRequired: d?.training?.certificationRequired ?? 'No',
      customTrainingModules: d?.training?.customTrainingModules ?? '',
      customTrainingModulesFile: d?.training?.customTrainingModulesFile ?? null,
    });

    // Section 4: Nesting & Go-Live
    setNestingDuration(d?.nesting?.duration === '1w' || d?.nesting?.duration === '2w' || d?.nesting?.duration === 'custom' ? d.nesting.duration : 'null');
    setNestingCustomWeeks(Number.isFinite(+d?.nesting?.customWeeks) ? +d.nesting.customWeeks : 3);
    setSupervision(d?.nesting?.supervision ?? 'null');
    setReadiness(d?.nesting?.readiness ?? 'null');
    setPerformanceMonitoringFrequency(d?.nesting?.performanceMonitoringFrequency ?? 'Weekly');
    setClientInvolvementInNesting(d?.nesting?.clientInvolvementInNesting ?? 'Partial');
    setKnowledgeTransferMethod(d?.nesting?.knowledgeTransferMethod ?? 'Documentation');
    setEscalationProtocolDuringGoLive(d?.nesting?.escalationProtocolDuringGoLive ?? 'Weekly reports');

    // Section 5: White Label & Branding
    setWhiteLabel(!!d?.whiteLabel?.enabled);
    setBrandingNotes(d?.whiteLabel?.brandingNotes ?? '');

    // Section 6: Budget & Notes
    setBudgetRange(d.budgetRange ?? '');
    setMaxBudgetCap(d.maxBudgetCap ?? '');
    setPaymentPreference(d.paymentPreference ?? 'Monthly');
    setSpecialInstructions(d.specialInstructions ?? '');
    setTop3DeploymentGoals(d.top3DeploymentGoals ?? '');
    setSuccessMetricsKPIs(d.successMetricsKPIs ?? '');
    setPointOfContact({
      fullName: d?.pointOfContact?.fullName ?? '',
      email: d?.pointOfContact?.email ?? '',
      phoneWhatsapp: d?.pointOfContact?.phoneWhatsapp ?? '',
    });

    setStep(d._step ?? 0);
  }, [isOpen]); // load when modal is opened

  // -------- Save / Clear Draft --------
  const snapshot = () => ({
    // Section 1: Deployment Type
    deployType,
    expectedDuration,
    engagementDuration,
    industryProjectType,
    engagementModelAlignment,
    reportingLine,
    reportingLineOther,
    
    // Section 2: Talent Requirements
    roles,
    talentsNeeded,
    timeline,
    targetStart,
    seniorityLevel,
    workArrangement,
    shiftRequirement,
    languagesRequired,
    toolsSoftwareRequired,
    toolsSoftwareOther,
    securityComplianceNeeds,
    securityComplianceOther,
    portfolioReviewRequired,
    
    // Section 3: Training & Development
    training: { ...training },
    
    // Section 4: Nesting & Go-Live
    nesting: {
      duration: nestingDuration,
      customWeeks: nestingCustomWeeks,
      supervision,
      readiness,
      performanceMonitoringFrequency,
      clientInvolvementInNesting,
      knowledgeTransferMethod,
      escalationProtocolDuringGoLive,
    },
    
    // Section 5: White Label & Branding
    whiteLabel: { enabled: whiteLabel, brandingNotes },
    
    // Section 6: Budget & Notes
    budgetRange,
    maxBudgetCap,
    paymentPreference,
    specialInstructions,
    top3DeploymentGoals,
    successMetricsKPIs,
    pointOfContact,
    
    _step: step,
  });

  const saveDraft = () => {
    saveJSON(draftKey, snapshot());
    alert('Draft saved ✨');
  };

  const clearDraft = () => {
    try { localStorage.removeItem(draftKey); } catch {}
    // Reset all state
    // Section 1: Deployment Type
    setDeployType('');
    setExpectedDuration('Standard [30 days]');
    setEngagementDuration('Mid-Term [6–12 months]');
    setIndustryProjectType('Game Development');
    setEngagementModelAlignment('Linked to Pre-Onboarding');
    setReportingLine('Client PM');
    setReportingLineOther('');
    
    // Section 2: Talent Requirements
    setRoles([]);
    setTalentsNeeded('');
    setTimeline('');
    setTargetStart('');
    setSeniorityLevel('Mid-Level');
    setWorkArrangement('Remote');
    setShiftRequirement('Day');
    setLanguagesRequired([]);
    setToolsSoftwareRequired([]);
    setToolsSoftwareOther('');
    setSecurityComplianceNeeds([]);
    setSecurityComplianceOther('');
    setPortfolioReviewRequired('Yes');
    
    // Section 3: Training & Development
    setTraining({
      companyOrientation: false,
      clientProjectOrientation: false,
      systemsToolsTraining: false,
      systemsToolsDetails: '',
      roleSpecificSkillsTraining: false,
      roleSpecificSkillsDetails: '',
      softSkillsDevelopment: false,
      practicalApplicationSimulation: false,
      practicalApplicationDetails: '',
      assessmentEvaluation: false,
      assessmentEvaluationFile: null,
      trainingDeliveryMethod: 'Virtual',
      trainingDuration: 1,
      evaluationMethod: 'Both',
      certificationRequired: 'No',
      customTrainingModules: '',
      customTrainingModulesFile: null,
    });
    
    // Section 4: Nesting & Go-Live
    setNestingDuration('null');
    setNestingCustomWeeks(3);
    setSupervision('null');
    setReadiness('null');
    setPerformanceMonitoringFrequency('Weekly');
    setClientInvolvementInNesting('Partial');
    setKnowledgeTransferMethod('Documentation');
    setEscalationProtocolDuringGoLive('Weekly reports');
    
    // Section 5: White Label & Branding
    setWhiteLabel(false);
    setBrandingNotes('');
    
    // Section 6: Budget & Notes
    setBudgetRange('');
    setMaxBudgetCap('');
    setPaymentPreference('Monthly');
    setSpecialInstructions('');
    setTop3DeploymentGoals('');
    setSuccessMetricsKPIs('');
    setPointOfContact({
      fullName: '',
      email: '',
      phoneWhatsapp: '',
    });
    
    setErrors({});
    setStep(0);
    alert('Draft cleared.');
  };

  // -------- UI helpers --------
  const toggleRole = (name) =>
    setRoles((prev) => (prev.includes(name) ? prev.filter((r) => r !== name) : [...prev, name]));

  const toggleMultiSelect = (currentArray, setter, value) => {
    setter((prev) => (prev.includes(value) ? prev.filter((item) => item !== value) : [...prev, value]));
  };

  const Check = ({ label, checked, onChange }) => (
    <label className="drf-check">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span className="drf-checkmark" /> {label}
    </label>
  );

  const Radio = ({ name, value, selected, onChange, label }) => (
    <label className="drf-radio">
      <input
        type="radio"
        name={name}
        value={value}
        checked={selected === value}
        onChange={() => onChange(value)}
      />
      <span className="drf-radiomark" /> {label}
    </label>
  );

  // -------- Routing + Persist (submit) --------
  const saveCRMRecord = (payload) => {
    try {
      const key = 'CRM_RECORDS_DRF_v1';
      const all = JSON.parse(localStorage.getItem(key) || '[]');
      all.push({ ...payload, createdAt: new Date().toISOString() });
      localStorage.setItem(key, JSON.stringify(all));

      const taskKey = 'CRM_TASKS_v1';
      const tasks = JSON.parse(localStorage.getItem(taskKey) || '[]');
      tasks.push({
        type: 'AE_CALL',
        title: 'Call client re: Deployment Request',
        priority: payload.routing.opsPriority === 'FAST_TRACK' ? 'HIGH' : 'NORMAL',
        route: payload.routing,
        createdAt: new Date().toISOString(),
      });
      localStorage.setItem(taskKey, JSON.stringify(tasks));
    } catch {}
  };

  // -------- Validation per step (light, focused) --------
  const validateStep = (s) => {
    const e = {};
    if (s === 0) {
      if (!deployType) e.deployType = 'Please select a Deployment Type.';
      if (!expectedDuration) e.expectedDuration = 'Please select Expected Duration.';
      if (!engagementDuration) e.engagementDuration = 'Please select Engagement Duration.';
      if (!industryProjectType) e.industryProjectType = 'Please select Industry/Project Type.';
      if (!engagementModelAlignment) e.engagementModelAlignment = 'Please select Engagement Model Alignment.';
      if (!reportingLine) e.reportingLine = 'Please select Reporting Line.';
      if (reportingLine === 'Other' && !reportingLineOther.trim()) e.reportingLineOther = 'Please specify reporting line.';
    }
    if (s === 1) {
      if (roles.length === 0) e.roles = 'Select at least one Role.';
      if (!talentsNeeded || Number(talentsNeeded) < 1) e.talentsNeeded = 'Enter number of talents (≥ 1).';
      if (!timeline) e.timeline = 'Select a timeline/urgency.';
      if (!seniorityLevel) e.seniorityLevel = 'Please select Seniority Level.';
      if (!workArrangement) e.workArrangement = 'Please select Work Arrangement.';
      if (!shiftRequirement) e.shiftRequirement = 'Please select Shift Requirement.';
      if (!portfolioReviewRequired) e.portfolioReviewRequired = 'Please select Portfolio Review requirement.';
    }
    if (s === 2) {
      if (!training.trainingDeliveryMethod) e.trainingDeliveryMethod = 'Please select Training Delivery Method.';
      if (!Number.isFinite(+training.trainingDuration) || +training.trainingDuration < 1) {
        e.trainingDuration = 'Please enter valid training duration (≥ 1).';
      }
      if (!training.evaluationMethod) e.evaluationMethod = 'Please select Evaluation Method.';
      if (!training.certificationRequired) e.certificationRequired = 'Please select Certification requirement.';
    }
    if (s === 3) {
      if (nestingDuration === 'custom' && (!Number.isFinite(+nestingCustomWeeks) || +nestingCustomWeeks < 1)) {
        e.nestingCustomWeeks = 'Enter a valid custom duration in weeks (≥ 1).';
      }
      if (!performanceMonitoringFrequency) e.performanceMonitoringFrequency = 'Please select Performance Monitoring Frequency.';
      if (!clientInvolvementInNesting) e.clientInvolvementInNesting = 'Please select Client Involvement in Nesting.';
      if (!knowledgeTransferMethod) e.knowledgeTransferMethod = 'Please select Knowledge Transfer Method.';
      if (!escalationProtocolDuringGoLive) e.escalationProtocolDuringGoLive = 'Please select Escalation Protocol.';
    }
    if (s === 5) {
      if (!budgetRange.trim()) e.budgetRange = 'Please enter Budget Range.';
      if (!paymentPreference) e.paymentPreference = 'Please select Payment Preference.';
      if (!top3DeploymentGoals.trim()) e.top3DeploymentGoals = 'Please enter Top 3 Must-Have Deployment Goals.';
      if (!successMetricsKPIs.trim()) e.successMetricsKPIs = 'Please enter Success Metrics/KPIs.';
      if (!pointOfContact.fullName.trim()) e.pointOfContactFullName = 'Please enter Full Name.';
      if (!pointOfContact.email.trim()) e.pointOfContactEmail = 'Please enter Email Address.';
      if (!pointOfContact.phoneWhatsapp.trim()) e.pointOfContactPhone = 'Please enter Phone/WhatsApp.';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const goNext = () => { if (validateStep(step)) setStep((s) => Math.min(totalSteps - 1, s + 1)); };
  const goBack = () => setStep((s) => Math.max(0, s - 1));

  // Block Enter on mid steps (except textareas)
  const preventEnterSubmit = (e) => {
    if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA' && step < totalSteps - 1) {
      e.preventDefault();
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (step !== totalSteps - 1) return;   // only allow submit on last step
    if (!validateStep(step)) return;

    // routing rules
    const route = {
      salesFlag: whiteLabel || deployType === 'white-label',
      opsPriority: deployType === 'fast' ? 'FAST_TRACK' : 'STANDARD',
      enterpriseAE: Number(talentsNeeded) > 5,
    };

    const data = {
      // Section 1: Deployment Type
      deployType,
      expectedDuration,
      engagementDuration,
      industryProjectType,
      engagementModelAlignment,
      reportingLine,
      reportingLineOther,
      
      // Section 2: Talent Requirements
      roles,
      talentsNeeded: Number(talentsNeeded),
      timeline,
      targetStart,
      seniorityLevel,
      workArrangement,
      shiftRequirement,
      languagesRequired,
      toolsSoftwareRequired,
      toolsSoftwareOther,
      securityComplianceNeeds,
      securityComplianceOther,
      portfolioReviewRequired,
      
      // Section 3: Training & Development
      training,
      
      // Section 4: Nesting & Go-Live
      nesting: {
        duration: nestingDuration === 'custom' ? 'custom' : nestingDuration,
        weeks: nestingDuration === 'custom' ? Number(nestingCustomWeeks) : (nestingDuration === '1w' ? 1 : (nestingDuration === '2w' ? 2 : null)),
        supervision,
        readiness,
        performanceMonitoringFrequency,
        clientInvolvementInNesting,
        knowledgeTransferMethod,
        escalationProtocolDuringGoLive,
      },
      
      // Section 5: White Label & Branding
      whiteLabel: { enabled: whiteLabel, brandingNotes: brandingNotes.trim() },
      
      // Section 6: Budget & Notes
      budgetRange,
      maxBudgetCap,
      paymentPreference,
      specialInstructions,
      top3DeploymentGoals,
      successMetricsKPIs,
      pointOfContact,
      
      routing: route,
    };

    saveCRMRecord(data);

    const routeMsg = [
      route.salesFlag ? '• Sales flagged for White Label' : null,
      route.opsPriority === 'FAST_TRACK' ? '• Ops routed as FAST-TRACK' : null,
      route.enterpriseAE ? '• Routed to Enterprise AE' : null,
    ].filter(Boolean).join('\n') || '• Standard routing applied';

    // clear draft on successful submit
    try { localStorage.removeItem(draftKey); } catch {}

    alert(`Deployment Request saved.\n\nRouting:\n${routeMsg}`);
    onClose?.();
  };

  if (!isOpen) return null;

  return (
    <div className="drf-overlay" onClick={onClose}>
      <div className="drf-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <div className="drf-header">
          <h2 className="drf-title">Deployment Request Form</h2>
          <button className="drf-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        {/* Progress bar (reusing po- styles) */}
        <div className="po-progress">
          <div className="po-progress-top">
            <div className="po-progress-num">{progressPct}%</div>
          </div>
          <div className="po-progress-bar">
            <div className="po-progress-fill" style={{ width: `${progressPct}%` }} />
          </div>
        </div>

        <form ref={formRef} className="drf-body" onSubmit={onSubmit} onKeyDown={preventEnterSubmit}>
          {/* ===== STEP 1: Deployment Type ===== */}
          {step === 0 && (
            <section className="drf-card">
              <h3 className="drf-card-title">Section 1: Deployment Type</h3>
              
              <div className="po-group">
                <h4 className="po-h4">Deployment Type</h4>
                <div className="po-grid1">
                <Radio name="deployType" value="standard"    selected={deployType} onChange={setDeployType}
                  label="Standard Deployment (Training → Nesting → Go-Live)" />
                <Radio name="deployType" value="fast"        selected={deployType} onChange={setDeployType}
                  label="Fast-Track Deployment (14-day accelerated)" />
                <Radio name="deployType" value="white-label" selected={deployType} onChange={setDeployType}
                  label="White Label Deployment (client-branded delivery; Sales engagement)" />
              </div>
              {errors.deployType && <div className="po-error">{errors.deployType}</div>}
              </div>

              <div className="po-section-divider"></div>

              <div className="po-group">
                <h4 className="po-h4">Deployment Details</h4>
                <p className="po-subtitle">Configure the deployment parameters and engagement model</p>
                
                <div className="po-grid3">
                  <div className="po-field">
                    <label className="po-label">
                      Expected Duration of Deployment 
                      <span className="req-star">*</span>
                    </label>
                    <select
                      className="po-input po-select-dark"
                      value={expectedDuration}
                      onChange={(e) => setExpectedDuration(e.target.value)}
                      required
                    >
                      <option value="Standard [30 days]">Standard [30 days]</option>
                      <option value="Fast-Track [14 days]">Fast-Track [14 days]</option>
                      <option value="Custom">Custom</option>
                    </select>
                    {errors.expectedDuration && <div className="po-error">{errors.expectedDuration}</div>}
                  </div>

                  <div className="po-field">
                    <label className="po-label">
                      Engagement Duration 
                      <span className="req-star">*</span>
                    </label>
                    <select
                      className="po-input po-select-dark"
                      value={engagementDuration}
                      onChange={(e) => setEngagementDuration(e.target.value)}
                      required
                    >
                      <option value="Short-Term [&lt;6 months]">Short-Term [&lt;6 months]</option>
                      <option value="Mid-Term [6–12 months]">Mid-Term [6–12 months]</option>
                      <option value="Long-Term [12+ months]">Long-Term [12+ months]</option>
                      <option value="Indefinite">Indefinite</option>
                    </select>
                    {errors.engagementDuration && <div className="po-error">{errors.engagementDuration}</div>}
                  </div>

                  <div className="po-field">
                    <label className="po-label">
                      Industry / Project Type 
                      <span className="req-star">*</span>
                    </label>
                    <select
                      className="po-input po-select-dark"
                      value={industryProjectType}
                      onChange={(e) => setIndustryProjectType(e.target.value)}
                      required
                    >
                      <option value="Game Development">Game Development</option>
                      <option value="Animation">Animation</option>
                      <option value="VFX">VFX</option>
                      <option value="Tech">Tech</option>
                      <option value="Other">Other</option>
                    </select>
                    {errors.industryProjectType && <div className="po-error">{errors.industryProjectType}</div>}
                  </div>
                </div>

                <div className="po-grid2">
                  <div className="po-field">
                    <label className="po-label">
                      Engagement Model Alignment 
                      <span className="req-star">*</span>
                    </label>
                    <select
                      className="po-input po-select-dark"
                      value={engagementModelAlignment}
                      onChange={(e) => setEngagementModelAlignment(e.target.value)}
                      required
                    >
                      <option value="Linked to Pre-Onboarding">Linked to Pre-Onboarding</option>
                      <option value="Linked to Onboarding">Linked to Onboarding</option>
                      <option value="Standalone Deployment">Standalone Deployment</option>
                    </select>
                    {errors.engagementModelAlignment && <div className="po-error">{errors.engagementModelAlignment}</div>}
                  </div>

                  <div className="po-field">
                    <label className="po-label">
                      Reporting Line 
                      <span className="req-star">*</span>
                    </label>
                    <select
                      className="po-input po-select-dark"
                      value={reportingLine}
                      onChange={(e) => setReportingLine(e.target.value)}
                      required
                    >
                      <option value="Client PM">Client PM</option>
                      <option value="Exodia Supervisor">Exodia Supervisor</option>
                      <option value="Joint Supervision">Joint Supervision</option>
                      <option value="Other">Other</option>
                    </select>
                    {errors.reportingLine && <div className="po-error">{errors.reportingLine}</div>}
                  </div>
                </div>

                {reportingLine === 'Other' && (
                  <div className="po-field">
                    <label className="po-label">
                      Please specify reporting line
                    </label>
                    <input
                      className="po-input"
                      placeholder="Enter reporting line details..."
                      value={reportingLineOther}
                      onChange={(e) => setReportingLineOther(e.target.value)}
                      required
                    />
                    {errors.reportingLineOther && <div className="po-error">{errors.reportingLineOther}</div>}
                  </div>
                )}
              </div>
            </section>
          )}

          {/* ===== STEP 2: Talent Requirements ===== */}
          {step === 1 && (
            <section className="drf-card">
              <h3 className="drf-card-title">Section 2: Talent Requirements</h3>

              <div className="po-group">
                <h4 className="po-h4">Roles for Deployment</h4>
                <div className="po-checklist">
                {rolesCatalog.map((r) => (
                    <label key={r} className={`po-check ${roles.includes(r) ? 'active' : ''}`}>
                    <input type="checkbox" checked={roles.includes(r)} onChange={() => toggleRole(r)} />
                      <span className="po-checkmark" /> {r}
                  </label>
                ))}
              </div>
              {errors.roles && <div className="po-error">{errors.roles}</div>}
              </div>

              <div className="po-section-divider"></div>

              <div className="po-group">
                <h4 className="po-h4">Basic Requirements</h4>
                <p className="po-subtitle">Specify the basic talent requirements and timeline</p>
                
                <div className="po-grid3">
                  <div className="po-field">
                    <label className="po-label">
                      Number of Talents Needed 
                      <span className="req-star">*</span>
                    </label>
                  <input
                      type="number" min="1" className="po-input"
                    value={talentsNeeded}
                    onChange={(e) => setTalentsNeeded(e.target.value)}
                      required
                  />
                  {errors.talentsNeeded && <div className="po-error">{errors.talentsNeeded}</div>}
                  </div>

                  <div className="po-field">
                    <label className="po-label">
                      Timeline / Urgency 
                      <span className="req-star">*</span>
                    </label>
                    <select className="po-input po-select-dark" value={timeline} onChange={(e) => setTimeline(e.target.value)} required>
                    <option value="" disabled>Choose…</option>
                    <option value="urgent">Urgent</option>
                    <option value="standard">Standard</option>
                    <option value="flexible">Flexible</option>
                  </select>
                  {errors.timeline && <div className="po-error">{errors.timeline}</div>}
                  </div>

                  <div className="po-field">
                    <label className="po-label">Target Start Date (optional)</label>
                    <input type="date" className="po-input" value={targetStart} onChange={(e) => setTargetStart(e.target.value)} />
                  </div>
                </div>
              </div>

              <div className="po-section-divider"></div>

              <div className="po-group">
                <h4 className="po-h4">Talent Profile Requirements</h4>
                <p className="po-subtitle">Define the specific requirements for deployed talents</p>
                
                <div className="po-grid3">
                  <div className="po-field">
                    <label className="po-label">
                      Seniority Level Needed 
                      <span className="req-star">*</span>
                </label>
                    <select
                      className="po-input po-select-dark"
                      value={seniorityLevel}
                      onChange={(e) => setSeniorityLevel(e.target.value)}
                      required
                    >
                      <option value="Junior">Junior</option>
                      <option value="Mid-Level">Mid-Level</option>
                      <option value="Senior">Senior</option>
                    </select>
                    {errors.seniorityLevel && <div className="po-error">{errors.seniorityLevel}</div>}
                  </div>

                  <div className="po-field">
                    <label className="po-label">
                      Work Arrangement 
                      <span className="req-star">*</span>
                </label>
                    <select
                      className="po-input po-select-dark"
                      value={workArrangement}
                      onChange={(e) => setWorkArrangement(e.target.value)}
                      required
                    >
                      <option value="Onsite">Onsite</option>
                      <option value="Hybrid">Hybrid</option>
                      <option value="Remote">Remote</option>
                    </select>
                    {errors.workArrangement && <div className="po-error">{errors.workArrangement}</div>}
                  </div>

                  <div className="po-field">
                    <label className="po-label">
                      Shift Requirement 
                      <span className="req-star">*</span>
                    </label>
                    <select
                      className="po-input po-select-dark"
                      value={shiftRequirement}
                      onChange={(e) => setShiftRequirement(e.target.value)}
                      required
                    >
                      <option value="Day">Day</option>
                      <option value="Night">Night</option>
                      <option value="Flexible">Flexible</option>
                    </select>
                    {errors.shiftRequirement && <div className="po-error">{errors.shiftRequirement}</div>}
                  </div>
                </div>
              </div>

              <div className="po-section-divider"></div>

              <div className="po-group">
                <h4 className="po-h4">Languages Required</h4>
                <p className="po-subtitle">Select the languages required for this deployment</p>
                
                <div className="po-checklist">
                  {['English', 'Japanese', 'Korean', 'Mandarin', 'Spanish'].map((lang) => (
                    <label key={lang} className={`po-check ${languagesRequired.includes(lang) ? 'active' : ''}`}>
                      <input 
                        type="checkbox" 
                        checked={languagesRequired.includes(lang)} 
                        onChange={() => toggleMultiSelect(languagesRequired, setLanguagesRequired, lang)} 
                      />
                      <span className="po-checkmark" /> {lang}
                    </label>
                  ))}
                </div>
              </div>

              <div className="po-section-divider"></div>

              <div className="po-group">
                <h4 className="po-h4">Tools/Software Required</h4>
                <p className="po-subtitle">Select the tools and software required for this deployment</p>
                
                <div className="po-checklist">
                  {['Unity', 'Unreal', 'Jira', 'Blender', 'Photoshop'].map((tool) => (
                    <label key={tool} className={`po-check ${toolsSoftwareRequired.includes(tool) ? 'active' : ''}`}>
                      <input 
                        type="checkbox" 
                        checked={toolsSoftwareRequired.includes(tool)} 
                        onChange={() => toggleMultiSelect(toolsSoftwareRequired, setToolsSoftwareRequired, tool)} 
                      />
                      <span className="po-checkmark" /> {tool}
                    </label>
                  ))}
                  <label className={`po-check ${toolsSoftwareRequired.includes('Other') ? 'active' : ''}`}>
                    <input 
                      type="checkbox" 
                      checked={toolsSoftwareRequired.includes('Other')} 
                      onChange={() => toggleMultiSelect(toolsSoftwareRequired, setToolsSoftwareRequired, 'Other')} 
                    />
                    <span className="po-checkmark" /> Other
                  </label>
                </div>

                {toolsSoftwareRequired.includes('Other') && (
                  <div className="po-field">
                    <label className="po-label">Please specify other tools/software</label>
                    <input
                      className="po-input"
                      placeholder="Enter other tools/software..."
                      value={toolsSoftwareOther}
                      onChange={(e) => setToolsSoftwareOther(e.target.value)}
                    />
                  </div>
                )}
              </div>

              <div className="po-section-divider"></div>

              <div className="po-group">
                <h4 className="po-h4">Security/Compliance Needs</h4>
                <p className="po-subtitle">Select the security and compliance requirements</p>
                
                <div className="po-checklist">
                  {['NDA signing', 'IP restrictions', 'Limited access'].map((need) => (
                    <label key={need} className={`po-check ${securityComplianceNeeds.includes(need) ? 'active' : ''}`}>
                      <input 
                        type="checkbox" 
                        checked={securityComplianceNeeds.includes(need)} 
                        onChange={() => toggleMultiSelect(securityComplianceNeeds, setSecurityComplianceNeeds, need)} 
                      />
                      <span className="po-checkmark" /> {need}
                    </label>
                  ))}
                  <label className={`po-check ${securityComplianceNeeds.includes('Other') ? 'active' : ''}`}>
                    <input 
                      type="checkbox" 
                      checked={securityComplianceNeeds.includes('Other')} 
                      onChange={() => toggleMultiSelect(securityComplianceNeeds, setSecurityComplianceNeeds, 'Other')} 
                    />
                    <span className="po-checkmark" /> Other
                  </label>
                </div>

                {securityComplianceNeeds.includes('Other') && (
                  <div className="po-field">
                    <label className="po-label">Please specify other security/compliance needs</label>
                    <input
                      className="po-input"
                      placeholder="Enter other security/compliance needs..."
                      value={securityComplianceOther}
                      onChange={(e) => setSecurityComplianceOther(e.target.value)}
                    />
                  </div>
                )}
              </div>

              <div className="po-section-divider"></div>

              <div className="po-group">
                <h4 className="po-h4">Portfolio/Work Samples Review Required</h4>
                <p className="po-subtitle">Specify if portfolio review is required</p>
                
                <div className="po-field">
                  <label className="po-label">
                    Portfolio/Work Samples Review Required 
                    <span className="req-star">*</span>
                  </label>
                  <select
                    className="po-input po-select-dark"
                    value={portfolioReviewRequired}
                    onChange={(e) => setPortfolioReviewRequired(e.target.value)}
                    required
                  >
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                  {errors.portfolioReviewRequired && <div className="po-error">{errors.portfolioReviewRequired}</div>}
                </div>
              </div>
            </section>
          )}

          {/* ===== STEP 3: Training & Development ===== */}
          {step === 2 && (
            <section className="drf-card">
              <h3 className="drf-card-title">Section 3: Training &amp; Development</h3>
              
              <div className="po-group">
                <h4 className="po-h4">Training Modules</h4>
                <p className="po-subtitle">Select the training modules required for this deployment</p>
                
                <div className="po-checklist">
                  <Check label="Company Orientation" checked={training.companyOrientation} onChange={(v)=>setTraining(s=>({...s,companyOrientation:v}))} />
                  <Check label="Client & Project Orientation" checked={training.clientProjectOrientation} onChange={(v)=>setTraining(s=>({...s,clientProjectOrientation:v}))} />
                  <Check label="Systems & Tools Training" checked={training.systemsToolsTraining} onChange={(v)=>setTraining(s=>({...s,systemsToolsTraining:v}))} />
                  <Check label="Role-Specific Skills Training" checked={training.roleSpecificSkillsTraining} onChange={(v)=>setTraining(s=>({...s,roleSpecificSkillsTraining:v}))} />
                  <Check label="Soft Skills Development" checked={training.softSkillsDevelopment} onChange={(v)=>setTraining(s=>({...s,softSkillsDevelopment:v}))} />
                  <Check label="Practical Application & Simulation" checked={training.practicalApplicationSimulation} onChange={(v)=>setTraining(s=>({...s,practicalApplicationSimulation:v}))} />
                  <Check label="Assessment & Evaluation" checked={training.assessmentEvaluation} onChange={(v)=>setTraining(s=>({...s,assessmentEvaluation:v}))} />
                </div>
              </div>

              <div className="po-section-divider"></div>

              <div className="po-group">
                <h4 className="po-h4">Training Details</h4>
                <p className="po-subtitle">Specify additional details for selected training modules</p>
                
                {training.systemsToolsTraining && (
                  <div className="po-field">
                    <label className="po-label">Systems & Tools Training Details</label>
                    <textarea
                      className="po-textarea"
                      rows={3}
                      placeholder="Specify the tools and systems to be covered..."
                      value={training.systemsToolsDetails}
                      onChange={(e) => setTraining(s => ({ ...s, systemsToolsDetails: e.target.value }))}
                    />
                  </div>
                )}

                {training.roleSpecificSkillsTraining && (
                  <div className="po-field">
                    <label className="po-label">Role-Specific Skills Training Details</label>
                    <textarea
                      className="po-textarea"
                      rows={3}
                      placeholder="Specify the skills to be covered..."
                      value={training.roleSpecificSkillsDetails}
                      onChange={(e) => setTraining(s => ({ ...s, roleSpecificSkillsDetails: e.target.value }))}
                    />
                  </div>
                )}

                {training.practicalApplicationSimulation && (
                  <div className="po-field">
                    <label className="po-label">Practical Application & Simulation Details</label>
                    <textarea
                      className="po-textarea"
                      rows={3}
                      placeholder="Specify any special cases or requirements..."
                      value={training.practicalApplicationDetails}
                      onChange={(e) => setTraining(s => ({ ...s, practicalApplicationDetails: e.target.value }))}
                    />
                  </div>
                )}

                {training.assessmentEvaluation && (
                  <div className="po-field">
                    <label className="po-label">Assessment & Evaluation File Upload</label>
                    <input
                      type="file"
                      className="po-input"
                      accept=".pdf,.doc,.docx,.txt"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          setTraining(s => ({ ...s, assessmentEvaluationFile: file }));
                        }
                      }}
                    />
                    {training.assessmentEvaluationFile && (
                      <div className="po-help">
                        <strong>Selected:</strong> {training.assessmentEvaluationFile.name}
                      </div>
                    )}
                    <div className="po-help">Upload custom evaluation criteria (PDF, DOC, DOCX, TXT)</div>
                  </div>
                )}
              </div>

              <div className="po-section-divider"></div>

              <div className="po-group">
                <h4 className="po-h4">Training Configuration</h4>
                <p className="po-subtitle">Configure the training delivery and evaluation methods</p>
                
                <div className="po-grid3">
                  <div className="po-field">
                    <label className="po-label">
                      Training Delivery Method 
                      <span className="req-star">*</span>
                    </label>
                    <select
                      className="po-input po-select-dark"
                      value={training.trainingDeliveryMethod}
                      onChange={(e) => setTraining(s => ({ ...s, trainingDeliveryMethod: e.target.value }))}
                      required
                    >
                      <option value="Virtual">Virtual</option>
                      <option value="Onsite">Onsite</option>
                      <option value="Hybrid">Hybrid</option>
                    </select>
                    {errors.trainingDeliveryMethod && <div className="po-error">{errors.trainingDeliveryMethod}</div>}
                  </div>

                  <div className="po-field">
                    <label className="po-label">
                      Training Duration (per module) 
                      <span className="req-star">*</span>
                    </label>
                    <input
                      type="number"
                      min="1"
                      className="po-input"
                      placeholder="Days/Weeks"
                      value={training.trainingDuration}
                      onChange={(e) => setTraining(s => ({ ...s, trainingDuration: parseInt(e.target.value) || 1 }))}
                      required
                    />
                    {errors.trainingDuration && <div className="po-error">{errors.trainingDuration}</div>}
                  </div>

                  <div className="po-field">
                    <label className="po-label">
                      Evaluation Method 
                      <span className="req-star">*</span>
                    </label>
                    <select
                      className="po-input po-select-dark"
                      value={training.evaluationMethod}
                      onChange={(e) => setTraining(s => ({ ...s, evaluationMethod: e.target.value }))}
                      required
                    >
                      <option value="Client assessment">Client assessment</option>
                      <option value="Exodia assessment">Exodia assessment</option>
                      <option value="Both">Both</option>
                    </select>
                    {errors.evaluationMethod && <div className="po-error">{errors.evaluationMethod}</div>}
                  </div>
                </div>

                <div className="po-grid2">
                  <div className="po-field">
                    <label className="po-label">
                      Certification/Proof of Training Required 
                      <span className="req-star">*</span>
                    </label>
                    <select
                      className="po-input po-select-dark"
                      value={training.certificationRequired}
                      onChange={(e) => setTraining(s => ({ ...s, certificationRequired: e.target.value }))}
                      required
                    >
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select>
                    {errors.certificationRequired && <div className="po-error">{errors.certificationRequired}</div>}
                  </div>
                </div>
              </div>

              <div className="po-section-divider"></div>

              <div className="po-group">
                <h4 className="po-h4">Custom Training Modules</h4>
                <p className="po-subtitle">Specify any custom training requirements</p>
                
                <div className="po-field">
                  <label className="po-label">Custom Training Modules Required</label>
                  <textarea
                    className="po-textarea"
                    rows={3}
                    placeholder="Describe any custom training modules or requirements..."
                    value={training.customTrainingModules}
                    onChange={(e) => setTraining(s => ({ ...s, customTrainingModules: e.target.value }))}
                  />
                </div>

                <div className="po-field">
                  <label className="po-label">Custom Training Modules File Upload</label>
                  <input
                    type="file"
                    className="po-input"
                    accept=".pdf,.doc,.docx,.txt"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        setTraining(s => ({ ...s, customTrainingModulesFile: file }));
                      }
                    }}
                  />
                  {training.customTrainingModulesFile && (
                    <div className="po-help">
                      <strong>Selected:</strong> {training.customTrainingModulesFile.name}
                    </div>
                  )}
                  <div className="po-help">Upload custom training materials (PDF, DOC, DOCX, TXT)</div>
                </div>
              </div>
            </section>
          )}

          {/* ===== STEP 4: Nesting & Go-Live ===== */}
          {step === 3 && (
            <section className="drf-card">
              <h3 className="drf-card-title">Section 4: Nesting &amp; Go-Live</h3>

              <div className="po-group">
                <h4 className="po-h4">Nesting Duration</h4>
                <div className="po-grid1">
                <Radio name="nesting" value="1w"     selected={nestingDuration} onChange={setNestingDuration} label="1 week" />
                <Radio name="nesting" value="2w"     selected={nestingDuration} onChange={setNestingDuration} label="2 weeks" />
                <Radio name="nesting" value="custom" selected={nestingDuration} onChange={setNestingDuration} label="Custom" />
              </div>

              {nestingDuration === 'custom' && (
                  <div className="po-field">
                    <label className="po-label">Custom duration (weeks)</label>
                    <input
                      type="number" min="1" className="po-input"
                      value={nestingCustomWeeks}
                      onChange={(e)=>setNestingCustomWeeks(Number(e.target.value))}
                    />
                  {errors.nestingCustomWeeks && <div className="po-error">{errors.nestingCustomWeeks}</div>}
                </div>
              )}
              </div>

              <div className="po-section-divider"></div>

              <div className="po-group">
                <h4 className="po-h4">Supervision Level</h4>
                <div className="po-grid1">
                <Radio name="supervision" value="light"     selected={supervision} onChange={setSupervision} label="Light" />
                <Radio name="supervision" value="moderate"  selected={supervision} onChange={setSupervision} label="Moderate" />
                <Radio name="supervision" value="intensive" selected={supervision} onChange={setSupervision} label="Intensive" />
                </div>
              </div>

              <div className="po-section-divider"></div>

              <div className="po-group">
                <h4 className="po-h4">Go-Live Readiness Confirmation</h4>
                <div className="po-grid1">
                <Radio name="readiness" value="client" selected={readiness} onChange={setReadiness} label="Client-led" />
                <Radio name="readiness" value="exodia" selected={readiness} onChange={setReadiness} label="Exodia-led" />
                </div>
              </div>

              <div className="po-section-divider"></div>

              <div className="po-group">
                <h4 className="po-h4">Performance Monitoring & Client Involvement</h4>
                <p className="po-subtitle">Configure monitoring frequency and client involvement during nesting</p>
                
                <div className="po-grid2">
                  <div className="po-field">
                    <label className="po-label">
                      Performance Monitoring Frequency 
                      <span className="req-star">*</span>
                    </label>
                    <select
                      className="po-input po-select-dark"
                      value={performanceMonitoringFrequency}
                      onChange={(e) => setPerformanceMonitoringFrequency(e.target.value)}
                      required
                    >
                      <option value="Daily">Daily</option>
                      <option value="Weekly">Weekly</option>
                      <option value="Bi-weekly">Bi-weekly</option>
                      <option value="Custom">Custom</option>
                    </select>
                    {errors.performanceMonitoringFrequency && <div className="po-error">{errors.performanceMonitoringFrequency}</div>}
                  </div>

                  <div className="po-field">
                    <label className="po-label">
                      Client Involvement in Nesting 
                      <span className="req-star">*</span>
                    </label>
                    <select
                      className="po-input po-select-dark"
                      value={clientInvolvementInNesting}
                      onChange={(e) => setClientInvolvementInNesting(e.target.value)}
                      required
                    >
                      <option value="Full">Full</option>
                      <option value="Partial">Partial</option>
                      <option value="None">None</option>
                    </select>
                    {errors.clientInvolvementInNesting && <div className="po-error">{errors.clientInvolvementInNesting}</div>}
                  </div>
                </div>
              </div>

              <div className="po-section-divider"></div>

              <div className="po-group">
                <h4 className="po-h4">Knowledge Transfer & Escalation</h4>
                <p className="po-subtitle">Define knowledge transfer methods and escalation protocols</p>
                
                <div className="po-grid2">
                  <div className="po-field">
                    <label className="po-label">
                      Knowledge Transfer Method 
                      <span className="req-star">*</span>
                    </label>
                    <select
                      className="po-input po-select-dark"
                      value={knowledgeTransferMethod}
                      onChange={(e) => setKnowledgeTransferMethod(e.target.value)}
                      required
                    >
                      <option value="Documentation">Documentation</option>
                      <option value="Shadowing">Shadowing</option>
                      <option value="Recorded sessions">Recorded sessions</option>
                      <option value="Other">Other</option>
                    </select>
                    {errors.knowledgeTransferMethod && <div className="po-error">{errors.knowledgeTransferMethod}</div>}
                  </div>

                  <div className="po-field">
                    <label className="po-label">
                      Escalation Protocol During Go-Live 
                      <span className="req-star">*</span>
                    </label>
                    <select
                      className="po-input po-select-dark"
                      value={escalationProtocolDuringGoLive}
                      onChange={(e) => setEscalationProtocolDuringGoLive(e.target.value)}
                      required
                    >
                      <option value="Daily check-ins">Daily check-ins</option>
                      <option value="Weekly reports">Weekly reports</option>
                      <option value="Emergency hotline">Emergency hotline</option>
                      <option value="Other">Other</option>
                    </select>
                    {errors.escalationProtocolDuringGoLive && <div className="po-error">{errors.escalationProtocolDuringGoLive}</div>}
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* ===== STEP 5: White Label & Branding ===== */}
          {step === 4 && (
            <section className="drf-card">
              <h3 className="drf-card-title">Section 5: White Label &amp; Branding</h3>
              <div className="drf-grid-1">
                <label className="drf-switch">
                  <input type="checkbox" checked={whiteLabel} onChange={(e)=>setWhiteLabel(e.target.checked)} />
                  <span className="drf-switch-ui" />
                  <span className="drf-switch-label">Require White Label?</span>
                </label>
                {whiteLabel && (
                  <label className="drf-field" style={{ marginTop: 8 }}>
                    <span className="drf-label">Branding notes / assets link</span>
                    <input
                      className="drf-input"
                      placeholder="e.g., Brand kit link, naming conventions, logo usage"
                      value={brandingNotes}
                      onChange={(e)=>setBrandingNotes(e.target.value)}
                    />
                  </label>
                )}
              </div>
            </section>
          )}

          {/* ===== STEP 6: Budget & Notes ===== */}
          {step === 5 && (
            <section className="drf-card">
              <h3 className="drf-card-title">Section 6: Budget &amp; Notes</h3>
              
              <div className="po-group">
                <h4 className="po-h4">Budget Information</h4>
                <p className="po-subtitle">Specify the budget range and payment preferences</p>
                
                <div className="po-grid2">
                  <div className="po-field">
                    <label className="po-label">
                      Budget Range (per role or per hire) 
                      <span className="req-star">*</span>
                    </label>
                  <input
                      className="po-input"
                    placeholder="e.g., $X–$Y per talent or total budget"
                      value={budgetRange}
                      onChange={(e)=>setBudgetRange(e.target.value)}
                      required
                    />
                    {errors.budgetRange && <div className="po-error">{errors.budgetRange}</div>}
                  </div>

                  <div className="po-field">
                    <label className="po-label">Maximum Budget Cap (optional)</label>
                    <input
                      type="number"
                      className="po-input"
                      placeholder="Enter maximum budget cap"
                      value={maxBudgetCap}
                      onChange={(e)=>setMaxBudgetCap(e.target.value)}
                    />
                  </div>
                </div>

                <div className="po-field">
                  <label className="po-label">
                    Payment Preference 
                    <span className="req-star">*</span>
                </label>
                  <select
                    className="po-input po-select-dark"
                    value={paymentPreference}
                    onChange={(e)=>setPaymentPreference(e.target.value)}
                    required
                  >
                    <option value="Per milestone">Per milestone</option>
                    <option value="Monthly">Monthly</option>
                    <option value="Per talent">Per talent</option>
                  </select>
                  {errors.paymentPreference && <div className="po-error">{errors.paymentPreference}</div>}
              </div>
              </div>

              <div className="po-section-divider"></div>

              <div className="po-group">
                <h4 className="po-h4">Deployment Goals & Success Metrics</h4>
                <p className="po-subtitle">Define the key goals and success criteria for this deployment</p>
                
                <div className="po-field">
                  <label className="po-label">
                    Top 3 Must-Have Deployment Goals 
                    <span className="req-star">*</span>
                  </label>
                  <textarea
                    className="po-textarea"
                    rows={3}
                    placeholder="e.g., Talent must reach production within 2 weeks, Complete onboarding within 30 days, Achieve 90% client satisfaction..."
                    value={top3DeploymentGoals}
                    onChange={(e)=>setTop3DeploymentGoals(e.target.value)}
                    required
                  />
                  {errors.top3DeploymentGoals && <div className="po-error">{errors.top3DeploymentGoals}</div>}
                </div>

                <div className="po-field">
                  <label className="po-label">
                    Success Metrics / KPIs 
                    <span className="req-star">*</span>
                </label>
                  <textarea
                    className="po-textarea"
                    rows={3}
                    placeholder="We consider deployment successful if..."
                    value={successMetricsKPIs}
                    onChange={(e)=>setSuccessMetricsKPIs(e.target.value)}
                    required
                  />
                  {errors.successMetricsKPIs && <div className="po-error">{errors.successMetricsKPIs}</div>}
                </div>
              </div>

              <div className="po-section-divider"></div>

              <div className="po-group">
                <h4 className="po-h4">Point of Contact for Deployment</h4>
                <p className="po-subtitle">Provide contact information for the deployment coordinator</p>
                
                <div className="po-grid3">
                  <div className="po-field">
                    <label className="po-label">
                      Full Name 
                      <span className="req-star">*</span>
                    </label>
                    <input
                      className="po-input"
                      placeholder="Enter full name"
                      value={pointOfContact.fullName}
                      onChange={(e) => setPointOfContact(s => ({ ...s, fullName: e.target.value }))}
                      required
                    />
                    {errors.pointOfContactFullName && <div className="po-error">{errors.pointOfContactFullName}</div>}
                  </div>

                  <div className="po-field">
                    <label className="po-label">
                      Email Address 
                      <span className="req-star">*</span>
                    </label>
                    <input
                      type="email"
                      className="po-input"
                      placeholder="Enter email address"
                      value={pointOfContact.email}
                      onChange={(e) => setPointOfContact(s => ({ ...s, email: e.target.value }))}
                      required
                    />
                    {errors.pointOfContactEmail && <div className="po-error">{errors.pointOfContactEmail}</div>}
                  </div>

                  <div className="po-field">
                    <label className="po-label">
                      Phone/WhatsApp 
                      <span className="req-star">*</span>
                    </label>
                    <input
                      type="tel"
                      className="po-input"
                      placeholder="Enter phone number"
                      value={pointOfContact.phoneWhatsapp}
                      onChange={(e) => setPointOfContact(s => ({ ...s, phoneWhatsapp: e.target.value }))}
                      required
                    />
                    {errors.pointOfContactPhone && <div className="po-error">{errors.pointOfContactPhone}</div>}
                  </div>
                </div>
              </div>

              <div className="po-section-divider"></div>

              <div className="po-group">
                <h4 className="po-h4">Special Instructions / Additional Requirements</h4>
                <p className="po-subtitle">Add any special instructions or additional requirements</p>
                
                <div className="po-field">
                  <label className="po-label">Special Instructions / Additional Requirements</label>
                  <textarea
                    className="po-textarea"
                    rows={4}
                    placeholder="Anything important we should know about this deployment..."
                    value={specialInstructions}
                    onChange={(e)=>setSpecialInstructions(e.target.value)}
                  />
                </div>
              </div>
            </section>
          )}

          {/* ---- Wizard footer (exact block you requested) ---- */}
          <div className="po-footer po-footer--wizard">
            <div className="po-footer-left">
              <button type="button" className="po-btn po-btn-ghost" onClick={saveDraft}>Save Draft</button>
              <button type="button" className="po-btn po-btn-ghost" onClick={clearDraft}>
                Clear Draft
              </button>
            </div>
            <div className="po-footer-right">
              <button type="button" className="po-btn po-btn-muted" onClick={onClose}>Cancel</button>
              {step > 0 && (
                <button type="button" className="po-btn" onClick={goBack}>Back</button>
              )}
              {step < totalSteps - 1 ? (
                <button
                  type="button"
                  className="po-btn po-btn-primary"
                  onClick={(e) => { e.preventDefault(); goNext(); }}
                  formNoValidate
                >
                  Next
                </button>
              ) : (
                <button type="submit" className="po-btn po-btn-primary">Submit</button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
